import { Plan, PlanFeatures } from "../types";

/*
 These feature flags work alongside `featureAvailability.ts` to determine if a feature is available on a plan.
 New features should be added to `featureAvailability.ts` INSTEAD of here.
 They can also be added here if they require a manual override via the admin panel.
*/

export const DEFAULT_FEATURES: PlanFeatures = {
    creditSystem: true,
    allowPrivateProjects: false,
    apiUpload: false,
    emailUpload: false,
    videoProcessing: false,
    healthCheck: false,
    clipSearch: false,
    outsourcedAnnotations: false,
    advancedTransforms: false,
    exportVersion: false,
    cloudMlIntegrations: false,
    tfjsInBrowser: "dev_only",
    edgeInference: "dev_only",
    embedInMobileApps: false,
    offlineMode: false,
    vpcDeployment: false,
    supportLevel: "None",
    annotationReview: false,
    workspaceInsights: false,
    classAttributes: false,
    advancedLabelAssist: false,
    deviceMonitoring: false,
    rbac: false,
    highComplexitySmartPoly: false,
    modelEvaluation: false,
    activeLearning: false,
    modelMonitoring: false,
    hipaaCompliance: false,
    sessionRecording: false,
    manageSourceVideo: false,
    refreshAnnotationsButton: false,
    unlimitedTimeDedicatedDeployments: false,
    billSelfHostedDeployments: true,
    projectFolders: false,
    restrictTopLevelFolderCreation: false,
    advancedApiKeys: false,
    useProjectFoldersForWorkflows: true,
    disableAutoInstantModelTraining: false,
    folderBilling: false,
    userMetadata: false,
    bucketMirroring: false,
    unrestrictedEmailProxy: false,
    hideUpgradeCTA: false,
    workflowBlockControls: false
};

export const PLAN_FEATURES: Record<Plan, Partial<PlanFeatures>> = {
    [Plan.Public]: {
        apiUpload: true,
        emailUpload: true,
        videoProcessing: true,
        healthCheck: true,
        clipSearch: true,
        exportVersion: true,
        tfjsInBrowser: "with_attribution",
        edgeInference: "with_attribution",
        embedInMobileApps: true
    },
    [Plan.UbpBasic]: {
        allowPrivateProjects: true,
        apiUpload: true,
        emailUpload: true,
        videoProcessing: true,
        healthCheck: true,
        advancedTransforms: true,
        exportVersion: true,
        cloudMlIntegrations: true,
        advancedLabelAssist: true,
        modelEvaluation: true,
        manageSourceVideo: true
    },
    [Plan.UbpCore]: {
        allowPrivateProjects: true,
        apiUpload: true,
        emailUpload: true,
        videoProcessing: true,
        healthCheck: true,
        advancedTransforms: true,
        exportVersion: true,
        cloudMlIntegrations: true,
        advancedLabelAssist: true,
        modelEvaluation: true,
        manageSourceVideo: true
    },
    [Plan.UbpGrowth]: {
        allowPrivateProjects: true,
        apiUpload: true,
        emailUpload: true,
        videoProcessing: true,
        healthCheck: true,
        advancedTransforms: true,
        exportVersion: true,
        cloudMlIntegrations: true,
        annotationReview: true,
        advancedLabelAssist: true,
        rbac: true,
        modelEvaluation: true,
        modelMonitoring: true,
        manageSourceVideo: true
    },
    [Plan.UbpPremiumTrial]: {
        allowPrivateProjects: true,
        apiUpload: true,
        emailUpload: true,
        videoProcessing: true,
        healthCheck: true,
        advancedTransforms: true,
        exportVersion: true,
        cloudMlIntegrations: true,
        advancedLabelAssist: true,
        rbac: true,
        modelEvaluation: true,
        modelMonitoring: true,
        manageSourceVideo: true
    },
    [Plan.EnterpriseGrowth]: {
        allowPrivateProjects: true,
        apiUpload: true,
        emailUpload: true,
        videoProcessing: true,
        healthCheck: true,
        advancedTransforms: true,
        exportVersion: true,
        cloudMlIntegrations: true,
        annotationReview: true,
        advancedLabelAssist: true,
        rbac: true,
        modelEvaluation: true,
        modelMonitoring: true,
        manageSourceVideo: true,
        deviceManagerV2: true,
        classAttributes: true
    },
    [Plan.Enterprise]: {
        allowPrivateProjects: true,
        apiUpload: true,
        emailUpload: true,
        videoProcessing: true,
        healthCheck: true,
        advancedTransforms: true,
        exportVersion: true,
        cloudMlIntegrations: true,
        annotationReview: true,
        advancedLabelAssist: true,
        rbac: true,
        modelEvaluation: true,
        modelMonitoring: true,
        manageSourceVideo: true,
        folderPermissions: false, // Add-on: Enterprise Access Control & Data Governance
        deviceManagerV2: true,
        classAttributes: true
    },
    [Plan.LegacySandbox]: {
        creditSystem: false,
        allowPrivateProjects: true,
        apiUpload: true,
        emailUpload: true,
        videoProcessing: true,
        edgeInference: "forbidden"
    },
    [Plan.LegacyStarter]: {
        creditSystem: false,
        allowPrivateProjects: true,
        apiUpload: true,
        emailUpload: true,
        videoProcessing: true,
        healthCheck: true,
        advancedTransforms: true,
        exportVersion: true,
        cloudMlIntegrations: true,
        edgeInference: "forbidden",
        modelEvaluation: true,
        manageSourceVideo: true,
        classAttributes: true
    },
    [Plan.Research]: {
        allowPrivateProjects: false,
        apiUpload: true,
        emailUpload: true,
        videoProcessing: true,
        healthCheck: true,
        clipSearch: true,
        advancedTransforms: true,
        exportVersion: true,
        tfjsInBrowser: "with_attribution",
        edgeInference: "with_attribution",
        embedInMobileApps: true
    },
    [Plan.UninitalizedWorkspace]: {}
} as any;

export const FEATURES_CONFIG: Record<Plan, PlanFeatures> = Object.keys(PLAN_FEATURES).reduce(
    (acc, plan) => {
        acc[plan] = { ...DEFAULT_FEATURES, ...PLAN_FEATURES[plan] };
        return acc;
    },
    {} as Record<Plan, PlanFeatures>
);
